import { ComponentProps, useState } from 'react'

import { DateRange, OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Upload } from '@factorialco/f0-react/icons/app'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import { BenefitsUsagePageDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { useActiveMarkets } from 'modules/benefits/hooks/useActiveMarkets'
import { SupportedMarket } from 'modules/benefits/lib/supportedMarkets'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

import { extractEmployeeData } from './extractEmployeeData'
import { EmployeeSpendingData } from './types'

type OneDataCollectionProps = ComponentProps<typeof OneDataCollection>
type TableVisualization = Extract<
  OneDataCollectionProps['visualizations'][number],
  { type: 'table' }
>

// Manual type since not currently exported by factorial-one-react
type ColumnType = Omit<TableVisualization['options']['columns'][number], 'render'> & {
  render: (
    item: EmployeeSpendingData
  ) =>
    | string
    | number
    | { type: 'person'; value: { firstName: string; lastName: string; src: string } }
    | undefined
}

interface UsageDataCollectionProps {
  includeTraining?: boolean
  includeHealthcare?: boolean
}

/**
 * HACK: This wrapper is a band-aid while we investigate a deficiency in the
 * useGraphqlDataSource hook, which isn't refetching when the dependencies
 * provided the hook change. When that is fixed we should remove this
 * wrapper component, and we can use the date navigation filter available
 * to OneDataCollection.
 */
// TODO: Now that we have an improved query hierarchy we can migrate to data collection's built-in date filter

export const UsageDataCollection = ({
  includeTraining = false,
  includeHealthcare = false,
}: UsageDataCollectionProps) => {
  const [showExportModal, setShowExportModal] = useState(false)

  const [range, setRange] = useState<DateRange>({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date()),
  })
  const startDate = range.from
  const endDate = range.to ?? endOfMonth(range.from)

  const { access } = useCurrentSession()
  const locale = access.locale || 'es'

  const { activeMarkets } = useActiveMarkets()

  const source = useGraphqlDataCollectionSource(
    {
      document: BenefitsUsagePageDataCollectionDocument,
      navigationFilters: {
        date: {
          defaultValue: range.from,
          granularity: 'month',
          type: 'date-navigator',
          max: new Date(),
        },
      },
      mapVariables: ({ navigationFilters, search }) => {
        const dateRange = navigationFilters?.date?.value ?? range

        setRange(dateRange)

        return {
          search,
          compensatedAfter: formatISO(startDate, { representation: 'date' }),
          compensatedBefore: formatISO(endDate, {
            representation: 'date',
          }),
          createdAfter: startDate.toISOString(),
          createdBefore: endDate.toISOString(),
        }
      },
      mapData: extractEmployeeData,
      mapPagination: (data) => ({
        total: data.benefits.benefitEmployeesConnection.totalCount ?? 0,
      }),
      search: {
        enabled: true,
      },
      primaryActions: () => ({
        label: i18n.t('common.export'),
        onClick: () => setShowExportModal(true),
        icon: Upload,
      }),
    },
    [range]
  )

  const columns = useUnsafeMemo(() => {
    return buildColumns(locale, activeMarkets, includeTraining, includeHealthcare)
  }, [locale, activeMarkets, includeTraining, includeHealthcare])

  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns,
              frozenColumns: 1,
            },
          },
        ]}
      />
      {showExportModal && (
        <ExportSidepanel
          resource="benefits.benefit_compensation"
          overrideFilters={{
            created_at: [startDate.toISOString(), endDate.toISOString()],
          }}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </>
  )
}

const formatCurrency = (amount: number, locale: string) => {
  if (amount === 0) {
    return '-'
  }

  return formattedAmount({
    amountCents: amount,
    currency: 'EUR',
    locale,
  })
}

const buildColumns = (
  locale: string,
  activeMarkets: SupportedMarket[],
  includeTraining: boolean,
  includeHealthcare: boolean
): ColumnType[] => {
  const cols: ColumnType[] = [
    {
      label: i18n.t('benefits.tabs.usage.page.table.columns.employee'),
      render: (item: EmployeeSpendingData) => {
        return {
          type: 'person' as const,
          value: {
            firstName: item.firstName,
            lastName: item.lastName,
            src: buildUrl(item.avatar?.url ?? ''),
          },
        }
      },
    },
  ]

  if (activeMarkets.includes('es')) {
    cols.push(
      {
        label: i18n.t('benefits.tabs.usage.page.table.columns.total_amount'),
        render: (item: EmployeeSpendingData) =>
          formatCurrency(
            Object.values(item.amounts).reduce((acc, curr) => acc + curr, 0),
            locale
          ),
      },
      {
        label: i18n.t('benefits.tabs.usage.page.table.columns.meals'),
        render: (item: EmployeeSpendingData) => formatCurrency(item.amounts.meals, locale),
      },
      {
        label: i18n.t('benefits.tabs.usage.page.table.columns.transport'),
        render: (item: EmployeeSpendingData) => formatCurrency(item.amounts.transportation, locale),
      },
      {
        label: i18n.t('benefits.tabs.usage.page.table.columns.childcare'),
        render: (item: EmployeeSpendingData) => formatCurrency(item.amounts.childcare, locale),
      }
    )

    if (includeTraining) {
      cols.push({
        label: i18n.t('benefits.tabs.usage.page.table.columns.training'),
        render: (item: EmployeeSpendingData) => formatCurrency(item.amounts.training, locale),
      })
    }

    if (includeHealthcare) {
      cols.push({
        label: i18n.t('benefits.homepage.static_benefits.es.health_insurance.title'),
        render: (item: EmployeeSpendingData) => formatCurrency(item.amounts.healthcare, locale),
      })
    }
  }

  if (activeMarkets.includes('pt') && !activeMarkets.includes('es')) {
    cols.push({
      label: i18n.t('benefits.tabs.usage.page.table.columns.meals'),
      render: (item: EmployeeSpendingData) => formatCurrency(item.amounts.meals, locale),
    })
  }
  return cols
}
